import type { TimingSheet as TimingSheetData } from "@szerviz/core";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { SourceLink, usePrintSheet } from "./FuseSheet";
import { Logo } from "./Logo";

/**
 * The own timing sheet (owner, 2026-11-01): the timing replacement in short, in the workshop's
 * language, drawn by Garagevo from a shared "timingSheet" fact (the AI read the best sources and
 * wrote the summary). The source's diagrams and PDFs stay links; nothing of them is copied.
 * "PDF / Print" prints only this sheet on A4.
 */
export function TimingSheet({ sheet, vehicle, unchecked, status, diagrams }: {
  sheet: TimingSheetData;
  /** The type path, e.g. "Škoda Octavia II 1.9 TDI". */
  vehicle: string;
  /** An AI sheet nobody confirmed yet: the "AI · check it" badge on paper. */
  unchecked: boolean;
  /** The state badge and the ✓ / ✗ votes on the whole sheet (not printed). */
  status?: ReactNode;
  /** The found diagrams (title, link), listed on the sheet as links only. */
  diagrams: { title: string; url: string }[];
}) {
  const { t } = useTranslation();
  const { printing, print } = usePrintSheet();
  return (
    <section className="fuse-sheet timing-sheet" aria-label={t("timing.title")}>
      <div className="fuse-tools">
        {status && <span className="fuse-status">{status}</span>}
        <button type="button" className="fuse-print" onClick={print}>
          {t("fuse.print")}
        </button>
      </div>
      <TimingSheetBody sheet={sheet} vehicle={vehicle} unchecked={unchecked} diagrams={diagrams} />
      {printing && createPortal(
        <div className="fuse-print-root">
          <TimingSheetBody sheet={sheet} vehicle={vehicle} unchecked={unchecked} diagrams={diagrams} print />
        </div>,
        document.body,
      )}
    </section>
  );
}

function TimingSheetBody({ sheet, vehicle, unchecked, diagrams, print }: {
  sheet: TimingSheetData;
  vehicle: string;
  unchecked: boolean;
  diagrams: { title: string; url: string }[];
  print?: boolean;
}) {
  const { t } = useTranslation();
  const main = sheet.sources.find((s) => s.role === "main") ?? sheet.sources[0];
  // the main source and at most one more: too much information confuses (owner, 2026-11-01)
  const compared = sheet.sources.filter((s) => s !== main).slice(0, 1);
  const drive = sheet.drive === "belt" ? t("timing.driveBelt") : sheet.drive === "chain" ? t("timing.driveChain") : sheet.drive === "gear" ? t("timing.driveGear") : "";
  let n = 0;
  const head = (key: string) => <h4 className="fuse-box-head">{++n}. {t(key)}</h4>;
  return (
    <div className="fuse-body">
      <header className="fuse-head">
        <Logo size={26} />
        <span className="muted small">{t("timing.kind")}</span>
      </header>
      <h3 className="fuse-title">
        {t("timing.title")}
        {unchecked && print && <span className="fuse-ai">{t("fuse.aiCheck")}</span>}
      </h3>
      <p className="fuse-car">{[vehicle, sheet.years].filter(Boolean).join(", ")}</p>
      {(drive || sheet.interval) && (
        <dl className="timing-key">
          {drive && <div><dt>{t("timing.drive")}</dt><dd>{drive}</dd></div>}
          {sheet.interval && <div><dt>{t("timing.interval")}</dt><dd>{sheet.interval}</dd></div>}
        </dl>
      )}
      <p className="fuse-note">{t("timing.note")}</p>
      {sheet.warnings.length > 0 && (
        <section className="timing-warn" aria-label={t("timing.warnings")}>
          <strong>⚠ {t("timing.warnings")}</strong>
          <ul>{sheet.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </section>
      )}
      {sheet.parts.length > 0 && (
        <section className="fuse-box">
          {head("timing.parts")}
          <ul className="timing-chips">{sheet.parts.map((x, i) => <li key={i}>{x}</li>)}</ul>
        </section>
      )}
      {sheet.tools.length > 0 && (
        <section className="fuse-box">
          {head("timing.tools")}
          <ul className="timing-list">{sheet.tools.map((x, i) => <li key={i}>{x}</li>)}</ul>
        </section>
      )}
      {sheet.steps.length > 0 && (
        <section className="fuse-box">
          {head("timing.steps")}
          <ol className="timing-steps">{sheet.steps.map((x, i) => <li key={i}>{x}</li>)}</ol>
        </section>
      )}
      {sheet.torques.length > 0 && (
        <section className="fuse-box">
          {head("timing.torques")}
          <table className="fuse-table timing-torques">
            <thead>
              <tr><th scope="col">{t("timing.colWhat")}</th><th scope="col">{t("timing.colTorque")}</th></tr>
            </thead>
            <tbody>
              {sheet.torques.map((x, i) => <tr key={i}><td>{x.what}</td><td className="timing-nm">{x.value}</td></tr>)}
            </tbody>
          </table>
        </section>
      )}
      {diagrams.length > 0 && (
        <section className="fuse-box">
          {head("timing.diagrams")}
          <ul className="timing-list">
            {diagrams.map((d) => <li key={d.url}>{d.title} · <SourceLink url={d.url} /></li>)}
          </ul>
        </section>
      )}
      <footer className="fuse-foot">
        {t("timing.drawn")}
        {main && <>{" "}{t("fuse.mainSource")} <SourceLink url={main.url} /></>}
        {compared.length > 0 && <>{"; "}{t("fuse.compared")} <SourceLink url={compared[0]!.url} /></>}
        {". "}
        {t("fuse.checkedBy")}
      </footer>
    </div>
  );
}
